/**
 * Dashboard design primitives.
 *
 * The rules these encode, so screens stay consistent:
 *
 *   1. Structure comes from hairlines and whitespace, never from colour. A card
 *      is a 1px border and a divider — no tinted panels, no coloured plates
 *      behind icons.
 *   2. Colour is spent on data only. Ink is the gray ramp; the accent teal is
 *      reserved for meters and marks that encode a value; green/red are
 *      reserved for direction-of-change and status, always paired with a glyph
 *      or a label so the meaning never rests on hue alone.
 *   3. Numbers are `tabular-nums` and optically tightened, so a column of
 *      figures lines up and a value that ticks over doesn't shift the layout.
 */
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../utils/cn';
import { CHANNEL_COLORS, STATUS_COLORS } from '../utils/chartTheme';
import {
  IconChevronDown,
  IconClose,
  IconDeltaDown,
  IconDeltaFlat,
  IconDeltaUp,
  IconRefresh,
} from './icons';

/* ── Page header ─────────────────────────────────────────────────────────── */

export const PageHeader = ({ title, description, children }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
    <div className="min-w-0">
      <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.01em] text-gray-900">
        {title}
      </h1>
      {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
    </div>
    {children && (
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{children}</div>
    )}
  </div>
);

/* ── Toolbar button ──────────────────────────────────────────────────────── */

export const ToolbarButton = ({ icon: Icon, children, className, ...props }) => (
  <button
    type="button"
    className={cn(
      'inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5',
      'text-[13px] font-medium text-gray-600 transition-colors',
      'hover:border-gray-300 hover:text-gray-900',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className
    )}
    {...props}
  >
    {Icon && <Icon size={15} aria-hidden="true" />}
    {children}
  </button>
);

export const RefreshButton = ({ busy, ...props }) => (
  <ToolbarButton {...props} disabled={busy || props.disabled}>
    <IconRefresh size={15} className={cn(busy && 'animate-spin')} aria-hidden="true" />
    Refresh
  </ToolbarButton>
);

/* ── Panel: the standard bordered section ────────────────────────────────── */

export const Panel = ({ title, description, action, children, className, bodyClassName }) => (
  <section className={cn('rounded-xl border border-gray-200 bg-white', className)}>
    {(title || action) && (
      <header className="flex items-start justify-between gap-4 px-5 pb-4 pt-5">
        <div className="min-w-0">
          {title && (
            <h2 className="text-[15px] font-semibold tracking-[-0.01em] text-gray-900">
              {title}
            </h2>
          )}
          {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </header>
    )}
    <div className={cn('px-5 pb-5', !title && 'pt-5', bodyClassName)}>{children}</div>
  </section>
);

/* ── Delta: direction-of-change, glyph + value, never colour alone ───────── */

export const Delta = ({ value, suffix = '', positiveIsGood = true, className }) => {
  const n = Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return null;

  const isFlat = n === 0;
  const isUp = n > 0;
  const isGood = positiveIsGood ? isUp : !isUp;

  const Glyph = isFlat ? IconDeltaFlat : isUp ? IconDeltaUp : IconDeltaDown;
  const tone = isFlat ? 'text-gray-400' : isGood ? 'text-emerald-600' : 'text-rose-600';

  return (
    <span
      className={cn(
        'inline-flex flex-shrink-0 items-center gap-0.5 text-xs font-semibold tabular-nums',
        tone,
        className
      )}
    >
      <Glyph size={11} weight={isFlat ? 'bold' : 'fill'} aria-hidden="true" />
      {isFlat ? '0' : Math.abs(n)}
      {suffix}
      <span className="sr-only">
        {isFlat ? 'no change' : isUp ? 'increase' : 'decrease'}
      </span>
    </span>
  );
};

/* ── Meter: a value as a proportion of a whole ───────────────────────────── */

const METER_TONES = {
  accent: CHANNEL_COLORS.sms,
  voice: CHANNEL_COLORS.voiceSms,
  ai: CHANNEL_COLORS.aiVoice,
  neutral: '#9aa1a9',
  good: STATUS_COLORS.good,
  critical: STATUS_COLORS.critical,
};

export const Meter = ({ value, tone = 'accent', size = 'md', className, label }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn(
        'w-full overflow-hidden rounded-full bg-gray-100',
        size === 'sm' ? 'h-1' : size === 'lg' ? 'h-2' : 'h-1.5',
        className
      )}
    >
      <div
        className="h-full rounded-full transition-[width] duration-700 ease-out"
        style={{ width: `${pct}%`, backgroundColor: METER_TONES[tone] || METER_TONES.accent }}
      />
    </div>
  );
};

/** A labelled meter row — the standard way to show a ranked distribution. */
export const MeterRow = ({ label, value, valueLabel, hint, tone = 'accent', rank }) => (
  <div>
    <div className="mb-1.5 flex items-baseline justify-between gap-3">
      <span className="flex min-w-0 items-baseline gap-2">
        {rank !== undefined && (
          <span className="w-4 flex-shrink-0 text-xs tabular-nums text-gray-300">{rank}</span>
        )}
        <span className="truncate text-[13px] font-medium text-gray-700">{label}</span>
      </span>
      <span className="flex flex-shrink-0 items-baseline gap-2 text-xs text-gray-400">
        {hint && <span>{hint}</span>}
        <span className="text-[13px] font-semibold tabular-nums text-gray-900">{valueLabel}</span>
      </span>
    </div>
    <Meter value={value} tone={tone} label={label} />
  </div>
);

/* ── Stat card: the KPI unit ─────────────────────────────────────────────── */

export const StatCard = ({
  label,
  value,
  icon: Icon,
  loading = false,
  footnote,
  delta,
  meter,
}) => {
  const hasFooter = Boolean(delta || footnote || meter);

  return (
    <div className="rounded-xl border border-gray-200 bg-white transition-colors duration-200 hover:border-gray-300">
      <div className="px-5 pb-4 pt-5">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="flex-shrink-0 text-gray-400" aria-hidden="true" />}
          <p className="truncate text-[13px] font-medium leading-none text-gray-500">{label}</p>
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="h-7 w-24 animate-pulse rounded bg-gray-100" />
          ) : (
            <p className="truncate text-[28px] font-semibold leading-none tracking-[-0.02em] text-gray-900 tabular-nums">
              {value}
            </p>
          )}
        </div>
      </div>

      {hasFooter && (
        <div className="flex min-h-[40px] items-center gap-2 border-t border-gray-100 px-5 py-2.5">
          {loading ? (
            <div className="h-3 w-32 animate-pulse rounded bg-gray-100" />
          ) : meter ? (
            <>
              <Meter
                value={meter.value}
                tone={meter.tone}
                size="sm"
                label={label}
                className="max-w-[96px]"
              />
              <span className="truncate text-xs text-gray-400">{footnote}</span>
            </>
          ) : (
            <>
              {delta && <Delta {...delta} />}
              {footnote && <span className="truncate text-xs text-gray-400">{footnote}</span>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Metric grid: hairline-separated tiles, replaces tinted boxes ────────── */

export const MetricGrid = ({ children, className }) => (
  <div
    className={cn(
      'grid gap-px overflow-hidden rounded-xl border border-gray-200 bg-gray-200',
      className
    )}
  >
    {children}
  </div>
);

export const MetricTile = ({ label, value, hint, dot, statusTone }) => (
  <div className="bg-white px-4 py-3.5">
    <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
      {dot && (
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
          style={{ backgroundColor: statusTone ? STATUS_COLORS[statusTone] : dot }}
        />
      )}
      <span className="truncate">{label}</span>
    </p>
    <p className="mt-1.5 text-[20px] font-semibold leading-none tracking-[-0.01em] text-gray-900 tabular-nums">
      {value}
    </p>
    {hint && <p className="mt-1.5 truncate text-xs text-gray-400">{hint}</p>}
  </div>
);

/* ── Section label: a channel heading tied to its series colour ──────────── */

export const SeriesLabel = ({ color, children, hint }) => (
  <div className="flex items-baseline gap-2">
    <span
      aria-hidden="true"
      className="h-2 w-2 flex-shrink-0 translate-y-[-1px] rounded-sm"
      style={{ backgroundColor: color }}
    />
    <h3 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-gray-700">
      {children}
    </h3>
    {hint && <span className="text-xs text-gray-400">{hint}</span>}
  </div>
);

/* ── Empty state ─────────────────────────────────────────────────────────── */

export const EmptyState = ({ icon: Icon, title, description, action, className }) => (
  <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
    {Icon && (
      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 text-gray-300">
        <Icon size={20} aria-hidden="true" />
      </span>
    )}
    <p className="mt-3 text-sm font-medium text-gray-700">{title}</p>
    {description && (
      <p className="mt-1 max-w-xs text-xs leading-relaxed text-gray-400">{description}</p>
    )}
    {action && <div className="mt-3 text-xs">{action}</div>}
  </div>
);

/* ── Inline loader ───────────────────────────────────────────────────────── */

export const LoadingBlock = ({ label = 'Loading…', className }) => (
  <div className={cn('flex flex-col items-center justify-center gap-3 text-gray-300', className)}>
    <IconRefresh size={22} className="animate-spin" aria-hidden="true" />
    <span className="text-xs text-gray-400">{label}</span>
  </div>
);

/* ── Select ──────────────────────────────────────────────────────────────── */

/**
 * A styled native `<select>`.
 *
 * Structure follows the shadcn/Radix select (trigger, value, trailing chevron,
 * focus ring), but stays a real `<select>` rather than a popover built from
 * listbox primitives. That keeps keyboard handling, typeahead, form semantics
 * and the native picker on mobile for free, and avoids pulling Radix and a
 * second icon library into a codebase that is plain Tailwind throughout.
 *
 * `appearance-none` removes the OS control so the chevron below is the only
 * affordance; without it the browser draws its own arrow as well.
 */
export const Select = ({ label, hint, className, containerClassName, children, ...props }) => {
  const id = React.useId();
  return (
    <div className={cn('min-w-0', containerClassName)}>
      {label && (
        <label
          htmlFor={id}
          className="mb-1.5 block text-xs font-medium leading-none text-gray-500"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={id}
          className={cn(
            'w-full appearance-none rounded-lg border border-gray-200 bg-white',
            'py-2 pl-3 pr-9 text-sm text-gray-900',
            'transition-colors hover:border-gray-300',
            'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
            'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400',
            className
          )}
          {...props}
        >
          {children}
        </select>
        <IconChevronDown
          size={14}
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
      </div>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
};

/* ── Status pill ─────────────────────────────────────────────────────────── */

/**
 * Status is carried by a label plus a dot, not by hue alone, and never in
 * all-caps — shouting is not hierarchy. Tones map to the same semantic ramp
 * the charts use.
 */
const PILL_TONES = {
  neutral: 'border-gray-200 text-gray-600',
  good:    'border-green-200 text-green-700',
  warn:    'border-amber-200 text-amber-700',
  bad:     'border-red-200 text-red-700',
  accent:  'border-primary-200 text-primary-700',
};

const PILL_DOTS = {
  neutral: 'bg-gray-300',
  good:    'bg-green-500',
  warn:    'bg-amber-500',
  bad:     'bg-red-500',
  accent:  'bg-primary-500',
};

export const StatusPill = ({ tone = 'neutral', dot = true, children, className }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full border bg-white px-2 py-0.5',
      'text-[11px] font-medium leading-5 whitespace-nowrap',
      PILL_TONES[tone] || PILL_TONES.neutral,
      className
    )}
  >
    {dot && (
      <span
        className={cn('h-1.5 w-1.5 flex-shrink-0 rounded-full', PILL_DOTS[tone] || PILL_DOTS.neutral)}
        aria-hidden="true"
      />
    )}
    {children}
  </span>
);

/* ── Icon button ─────────────────────────────────────────────────────────── */

/**
 * Row actions. Icons stay recessive grey and only pick up colour on hover, so
 * a table of twenty rows does not read as forty coloured marks competing with
 * the data. `tone="danger"` reddens on hover only.
 */
export const IconButton = ({ icon: Icon, label, tone = 'default', className, ...props }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    className={cn(
      'inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400',
      'transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/30',
      tone === 'danger'
        ? 'hover:bg-red-50 hover:text-red-600'
        : 'hover:bg-gray-100 hover:text-gray-700',
      className
    )}
    {...props}
  >
    <Icon size={15} aria-hidden="true" />
  </button>
);

/* ── Modal: an overlay that does not move the page underneath ────────────── */

/**
 * A dialog for content that would otherwise have to grow a card.
 *
 * The dashboard's activity feed is the case this exists for: expanding it in
 * place pushed everything below it off the first screen, and the card beside it
 * either stretched to match or fell out of alignment. An overlay leaves the
 * layout exactly where it was.
 *
 * What it handles that the hand-rolled overlays elsewhere in the app do not:
 * Escape closes it, the backdrop closes it but a click inside does not, the
 * page behind is locked so the background does not scroll under the dialog,
 * focus moves into the panel on open and returns to the trigger on close, and
 * it is announced as a dialog with its own title rather than as anonymous
 * divs.
 */
export const Modal = ({ open, onClose, title, description, children, className }) => {
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    // Remember what had focus so it can be handed back on close.
    returnFocusRef.current = document.activeElement;
    panelRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);

    // Lock the page behind the dialog. The scrollbar's width is added back as
    // padding so the layout doesn't jump sideways as it disappears.
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (gutter > 0) body.style.paddingRight = `${gutter}px`;

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
      returnFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => {
        // mousedown, not click: a drag that starts inside the panel and ends on
        // the backdrop should not count as dismissing it.
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl outline-none',
          className
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold tracking-[-0.01em] text-gray-900">{title}</h2>
            {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-m-1.5 flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-900"
          >
            <IconClose size={18} aria-hidden="true" />
          </button>
        </header>

        {/* Only this scrolls, so the header stays put on a long list. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-2">{children}</div>
      </div>
    </div>
  );
};

/* ── Avatar: the user's picture, or their initials ───────────────────────── */

/**
 * Falls back to initials when there is no picture, and again if the picture
 * fails to load — a stored avatar_url can outlive its file, and a broken image
 * icon in the top bar looks like the app is broken rather than the file.
 */
export const Avatar = ({ src, name = '', size = 32, className }) => {
  const [failed, setFailed] = useState(false);

  const initials =
    name
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'CA';

  const box = { width: size, height: size };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name || 'Profile picture'}
        style={box}
        onError={() => setFailed(true)}
        className={cn('flex-shrink-0 rounded-full object-cover', className)}
      />
    );
  }

  return (
    <span
      style={box}
      aria-hidden="true"
      className={cn(
        'flex flex-shrink-0 items-center justify-center rounded-full bg-primary-600 font-bold text-white',
        className
      )}
    >
      <span style={{ fontSize: Math.max(10, Math.round(size * 0.36)) }}>{initials}</span>
    </span>
  );
};
